import { LinearGradient } from 'expo-linear-gradient';
import {
  ArrowRight,
  Building2,
  CheckCircle,
  Mail,
  PartyPopper,
  Phone,
  User,
} from 'lucide-react-native';
import { useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useHoverState } from '../../hooks/useHoverState';
import { trackEvent } from '../../lib/analytics';
import { type WaitlistData, waitlistAPI } from '../../lib/api';
import { getAttribution, toAnalyticsAttribution } from '../../lib/attribution';
import { useMixpanel } from '../../lib/mixpanel';
import { colors } from '../../theme/colors';
import { withAlpha } from '../../theme/colorUtils';
import { easingCss } from '../../theme/motion';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { FadeIn } from '../animations';
import { Input } from '../primitives/Input';
import { Text } from '../primitives/Text';

const ICON_SIZE = 18;

interface WaitlistFormProps {
  onSuccess?: () => void;
}

export const WaitlistForm = ({ onSuccess }: WaitlistFormProps) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const mp = useMixpanel();
  const formStartedRef = useRef(false);

  const handleFieldFocus = (field: 'email' | 'name' | 'company' | 'phone') => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent(mp, 'landing.waitlist_form_started', { first_field: field });
  };
  const [formData, setFormData] = useState<WaitlistData>({
    email: '',
    fullName: '',
    company: '',
    phone: '',
  });
  const [errors, setErrors] = useState<{
    email?: string;
    fullName?: string;
    company?: string;
    phone?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\d\s\-+()]+$/;
    return phoneRegex.test(phone) && phone.replaceAll(/\D/g, '').length >= 10;
  };

  const validateName = (name: string): boolean => {
    return name.length >= 2 && name.length <= 100;
  };

  const validateCompany = (company: string): boolean => {
    return company.length >= 2 && company.length <= 100;
  };

  const handleSubmit = async () => {
    const newErrors: {
      email?: string;
      fullName?: string;
      company?: string;
      phone?: string;
    } = {};

    setErrors({});

    if (!formData.email.trim()) {
      newErrors.email = t('waitlist.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('waitlist.emailInvalid');
    }

    if (formData.fullName?.trim() && !validateName(formData.fullName.trim())) {
      newErrors.fullName = t('waitlist.nameInvalid');
    }

    if (formData.company?.trim() && !validateCompany(formData.company.trim())) {
      newErrors.company = t('waitlist.companyInvalid');
    }

    if (formData.phone?.trim() && !validatePhone(formData.phone.trim())) {
      newErrors.phone = t('waitlist.phoneInvalid');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      trackEvent(mp, 'landing.waitlist_validation_failed', {
        invalid_fields: Object.keys(newErrors),
        error_count: Object.keys(newErrors).length,
      });
      return;
    }

    const filledOptionalFields = (['fullName', 'company', 'phone'] as const)
      .filter((field) => formData[field]?.trim())
      .map((field) => (field === 'fullName' ? 'name' : field));

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const attribution = getAttribution();
    const analyticsAttribution = toAnalyticsAttribution(attribution);

    try {
      trackEvent(mp, 'landing.waitlist_submitted', {
        filled_optional_fields: filledOptionalFields,
        optional_field_count: filledOptionalFields.length,
        ...analyticsAttribution,
      });

      await waitlistAPI.join({
        email: formData.email.trim(),
        fullName: formData.fullName?.trim() || undefined,
        company: formData.company?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        ...attribution,
      });

      setSubmitStatus('success');
      setFormData({ email: '', fullName: '', company: '', phone: '' });
      showSuccess(t('waitlist.successMessage'));
      trackEvent(mp, 'landing.waitlist_success', {
        filled_optional_fields: filledOptionalFields,
        ...analyticsAttribution,
      });
      mp.register({ waitlisted: true });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: unknown) {
      console.error('Waitlist submission error:', err);

      let errorMsg = t('waitlist.errorGeneric');
      const error = err as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };

      if (error.response?.status === 409) {
        errorMsg = t('waitlist.alreadyRegistered');
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      const status = error.response?.status;
      const errorType: 'already_registered' | 'validation' | 'network' | 'unknown' =
        status === 409
          ? 'already_registered'
          : status === 400 || status === 422
            ? 'validation'
            : error.response
              ? 'unknown'
              : 'network';
      trackEvent(mp, 'landing.waitlist_failed', { error_type: errorType, status_code: status });

      showError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={styles.container}>
      {submitStatus === 'success' ? (
        <FadeIn delay={0} translateY={20}>
          <View style={styles.successContainer}>
            <View style={styles.successIconContainer}>
              <View style={styles.successIconGlow} />
              <View style={styles.successIcon}>
                <PartyPopper size={32} color={colors.primary[500]} />
              </View>
            </View>
            <Text variant="h3" style={styles.successTitle}>
              {t('waitlist.successTitle')}
            </Text>
            <Text variant="body" color={colors.text.secondary} style={styles.successMessage}>
              {t('waitlist.successMessage')}
            </Text>
            <View style={styles.successBadges}>
              <View style={styles.successBadge}>
                <CheckCircle size={14} color={colors.primary[500]} />
                <Text style={styles.successBadgeText}>
                  {t('components.landing.emailConfirmed')}
                </Text>
              </View>
              <View style={styles.successBadge}>
                <CheckCircle size={14} color={colors.primary[500]} />
                <Text style={styles.successBadgeText}>
                  {t('components.landing.earlyAccessSecured')}
                </Text>
              </View>
            </View>
          </View>
        </FadeIn>
      ) : (
        <View style={styles.formContainer}>
          <Input
            label={t('waitlist.emailLabel')}
            placeholder={t('waitlist.emailPlaceholder')}
            leftIcon={<Mail size={ICON_SIZE} color={colors.text.tertiary} strokeWidth={1.75} />}
            value={formData.email}
            onChangeText={(email) => setFormData({ ...formData, email })}
            onFocus={() => handleFieldFocus('email')}
            error={errors.email}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            editable={!isSubmitting}
          />

          <Input
            label={t('waitlist.nameLabel')}
            placeholder={t('waitlist.namePlaceholder')}
            leftIcon={<User size={ICON_SIZE} color={colors.text.tertiary} strokeWidth={1.75} />}
            value={formData.fullName}
            onChangeText={(fullName) => setFormData({ ...formData, fullName })}
            onFocus={() => handleFieldFocus('name')}
            error={errors.fullName}
            autoComplete="name"
            editable={!isSubmitting}
          />

          <Input
            label={t('waitlist.companyLabel')}
            placeholder={t('waitlist.companyPlaceholder')}
            leftIcon={
              <Building2 size={ICON_SIZE} color={colors.text.tertiary} strokeWidth={1.75} />
            }
            value={formData.company}
            onChangeText={(company) => setFormData({ ...formData, company })}
            onFocus={() => handleFieldFocus('company')}
            error={errors.company}
            editable={!isSubmitting}
          />

          <Input
            label={t('waitlist.phoneLabel')}
            placeholder={t('waitlist.phonePlaceholder')}
            leftIcon={<Phone size={ICON_SIZE} color={colors.text.tertiary} strokeWidth={1.75} />}
            value={formData.phone}
            onChangeText={(phone) => setFormData({ ...formData, phone })}
            onFocus={() => handleFieldFocus('phone')}
            error={errors.phone}
            keyboardType="phone-pad"
            autoComplete="tel"
            editable={!isSubmitting}
          />

          <SubmitButton
            loading={isSubmitting}
            label={t('landing.hero.primaryCta')}
            loadingLabel={t('waitlist.submitting')}
            onPress={handleSubmit}
          />
        </View>
      )}
    </View>
  );
};

const ctaTransition: ViewStyle = (Platform.select({
  web: {
    transitionProperty: 'transform, box-shadow',
    transitionDuration: '220ms',
    transitionTimingFunction: easingCss.out,
  },
  default: {},
}) ?? {}) as ViewStyle;

interface SubmitButtonProps {
  loading: boolean;
  label: string;
  loadingLabel: string;
  onPress: () => void;
}

function SubmitButton({ loading, label, loadingLabel, onPress }: Readonly<SubmitButtonProps>) {
  const { isHovered, hoverProps } = useHoverState();
  const scale = useRef(new Animated.Value(1)).current;

  const pressIn = () => {
    Animated.spring(scale, {
      toValue: 0.98,
      useNativeDriver: true,
      speed: 50,
      bounciness: 4,
    }).start();
  };
  const pressOut = () => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 50, bounciness: 4 }).start();
  };

  return (
    <Animated.View style={[styles.ctaWrap, { transform: [{ scale }] }]}>
      <Pressable
        accessibilityRole="button"
        disabled={loading}
        onPress={onPress}
        onPressIn={pressIn}
        onPressOut={pressOut}
        {...hoverProps}
        style={[styles.cta, ctaTransition, isHovered && styles.ctaHover]}
      >
        <LinearGradient
          colors={[colors.primary.neon, colors.primary[500], colors.secondary]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={colors['primary-foreground']} />
            <Text variant="body" style={styles.ctaText}>
              {loadingLabel}
            </Text>
          </View>
        ) : (
          <View style={styles.ctaContent}>
            <Text variant="body" style={styles.ctaText}>
              {label}
            </Text>
            <ArrowRight size={18} color={colors['primary-foreground']} strokeWidth={2.25} />
          </View>
        )}
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: Platform.select({ web: 500, default: undefined }),
  },
  formContainer: {
    gap: spacing.md,
  },
  ctaWrap: {
    marginTop: spacing.sm,
  },
  cta: {
    overflow: 'hidden',
    borderRadius: 10,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        boxShadow: `0 10px 26px -10px ${withAlpha(colors.primary[500], 0.4)}`,
      },
      default: {
        shadowColor: colors.primary[500],
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.4,
        shadowRadius: 16,
        elevation: 8,
      },
    }),
  },
  ctaHover: {
    transform: [{ translateY: -2 }],
    ...Platform.select({
      web: {
        boxShadow: `0 16px 34px -10px ${withAlpha(colors.primary[500], 0.6)}`,
      },
      default: {},
    }),
  },
  ctaContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  ctaText: {
    color: colors['primary-foreground'],
    fontWeight: '700',
    fontSize: 16,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  errorContainer: {
    backgroundColor: colors.status.error.bg,
    borderRadius: 8,
    padding: spacing.md,
  },
  errorText: {
    color: colors.status.error.dark,
  },
  successContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  successIconContainer: {
    position: 'relative',
    marginBottom: spacing.lg,
  },
  successIconGlow: {
    position: 'absolute',
    top: -10,
    left: -10,
    right: -10,
    bottom: -10,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.primary[500],
    opacity: 0.15,
  },
  successIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  successTitle: {
    marginBottom: spacing.sm,
    color: colors.text.primary,
    textAlign: 'center',
    fontSize: Platform.select({ web: 24, default: 22 }),
    fontWeight: '600',
  },
  successMessage: {
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.lg,
    maxWidth: 300,
  },
  successBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.md,
  },
  successBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary.muted,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  successBadgeText: {
    fontSize: 13,
    color: colors.primary[400],
    fontWeight: '500',
  },
});
